/**
 * Redemption review console — drill-in detail (build seq #4, Pass 4c-1, §3.3).
 *
 * The exception-path screen — required entry point for High redemptions (which
 * have no row-level approve in the queue). Renders the full /:id payload with a
 * FRESH live risk read, and offers the band-appropriate actions so a High case
 * can be actioned here. Mirrors AdminBuyerDetail (route page, back link, dark
 * cards) + the queue's reason-input + toast patterns.
 */
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Sparkles } from 'lucide-react';
import {
  fetchRedemptionDetail,
  approveRedemption,
  holdRedemption,
  rejectRedemption,
  type RiskBandValue,
} from '../../lib/adminApi';

function bandPillClass(band: RiskBandValue): string {
  if (band === 'high') return 'bg-red-500/15 text-red-300';
  if (band === 'medium') return 'bg-amber-500/15 text-amber-300';
  return 'bg-emerald-500/15 text-emerald-300';
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-slate-800 rounded-xl p-5 space-y-3">
      <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">{title}</h2>
      {children}
    </div>
  );
}

export default function AdminRedemptionDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [reasonKind, setReasonKind] = useState<'hold' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(null), 4000); }
  function clearReason() { setReasonKind(null); setReason(''); }

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'redemptions', 'detail', id],
    queryFn: () => fetchRedemptionDetail(id),
    enabled: !!id,
  });

  function invalidate() { void queryClient.invalidateQueries({ queryKey: ['admin', 'redemptions'] }); }

  const approveM = useMutation({ mutationFn: () => approveRedemption(id), onSuccess: (r) => { showToast(`Approved — ${r.status}`); invalidate(); }, onError: (e: Error) => showToast(e.message) });
  const holdM = useMutation({ mutationFn: () => holdRedemption(id, reason), onSuccess: () => { showToast('Held'); clearReason(); invalidate(); }, onError: (e: Error) => showToast(e.message) });
  const rejectM = useMutation({ mutationFn: () => rejectRedemption(id, reason.trim() || undefined), onSuccess: () => { showToast('Rejected — points refunded'); clearReason(); invalidate(); }, onError: (e: Error) => showToast(e.message) });
  const anyPending = approveM.isPending || holdM.isPending || rejectM.isPending;

  if (isLoading) return <div className="text-slate-400 text-sm">Loading…</div>;
  if (!data) return <div className="text-slate-400 text-sm">Redemption not found.</div>;

  const { redemption, member, risk, ai, actions, earn_summary, redemption_history, sessions, support_chats, trust_log, devices, audit_trail } = data;
  const band = risk?.band ?? redemption.risk_band ?? 'high';
  // §3.2 actions come straight from the server (same helper as the queue) — the
  // UI never re-derives the rule.
  const acts = actions;

  return (
    <div className="space-y-4">
      <Link to="/admin/redemptions" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white">
        <ArrowLeft className="w-3 h-3" /> Back to redemptions
      </Link>

      {/* Header + actions */}
      <div className="bg-slate-800 rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold font-display text-white">${redemption.usd_value.toFixed(2)} {redemption.vi_sku ?? ''}</h1>
              <span
                title="Review priority from redemption velocity, account age & value — not a fraud score. See the reason below."
                className={`text-xs px-2 py-0.5 rounded-full font-medium uppercase cursor-help ${bandPillClass(band)}`}
              >{band}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">{redemption.status}</span>
            </div>
            <p className="text-sm text-slate-400 mt-1">{member?.display_name ?? '—'} · {redemption.points_cost.toLocaleString()} pts</p>
            {risk?.reason && <p className="text-sm text-slate-300 mt-1">{risk.reason}</p>}
            {ai && ai.recommendation !== 'none' && (
              <p className="text-sm text-sky-300 mt-1 flex items-start gap-1">
                <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
                <span><span className="font-semibold uppercase">AI: {ai.recommendation}</span> — {ai.reasoning}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {reasonKind ? (
              <>
                <input
                  type="text" autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder={reasonKind === 'hold' ? 'Reason (required)' : 'Reason (optional)'}
                  className="w-48 px-2 py-1.5 rounded text-xs bg-slate-700 text-white border border-slate-600"
                />
                <button
                  onClick={() => (reasonKind === 'hold' ? holdM.mutate() : rejectM.mutate())}
                  disabled={anyPending || (reasonKind === 'hold' && !reason.trim())}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-40"
                >Confirm</button>
                <button onClick={clearReason} className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:text-white">Cancel</button>
              </>
            ) : (
              <>
                {acts.includes('approve') && (
                  <button onClick={() => approveM.mutate()} disabled={anyPending} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-900/50 text-emerald-300 hover:bg-emerald-900/70 disabled:opacity-40">Approve</button>
                )}
                {acts.includes('hold') && (
                  <button onClick={() => { setReasonKind('hold'); setReason(''); }} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-900/50 text-amber-300 hover:bg-amber-900/70">Hold</button>
                )}
                {acts.includes('reject') && (
                  <button onClick={() => { setReasonKind('reject'); setReason(''); }} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-900/50 text-red-300 hover:bg-red-900/70">Reject</button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Member */}
        <Card title="Member">
          {member ? (
            <dl className="text-sm space-y-1.5">
              <Row k="Name" v={member.display_name ?? '—'} />
              <Row k="Email" v={member.email ?? '—'} />
              <Row k="Trust" v={`${member.trust_tier_name} (${member.trust_score})`} />
              <Row k="Fraud" v={`${member.fraud_tier} (score ${member.fraud_score})`} />
              <Row k="Balance" v={`${member.total_points.toLocaleString()} pts`} />
              <Row k="Account age" v={`${member.account_age_days}d`} />
              <Row k="Status" v={member.status} />
            </dl>
          ) : <p className="text-slate-400 text-sm">Member not found.</p>}
        </Card>

        {/* Earn summary */}
        <Card title="Earn ↔ Redeem">
          <dl className="text-sm space-y-1.5">
            <Row k="Lifetime earned" v={`${earn_summary.lifetime_earned.toLocaleString()} pts`} />
            <Row k="Lifetime redeemed" v={`${earn_summary.lifetime_redeemed.toLocaleString()} pts`} />
            <Row k="Earn/redeem ratio" v={earn_summary.earn_to_redeem_ratio == null ? 'never redeemed' : `${earn_summary.earn_to_redeem_ratio}×`} />
          </dl>
        </Card>
      </div>

      {/* Risk signals (live) */}
      <Card title="Risk signals (live)">
        {risk && risk.signals.length ? (
          <div className="flex flex-wrap gap-2">
            {risk.signals.map((s, i) => (
              <span key={i} className={`text-xs px-2 py-1 rounded-lg ${bandPillClass(s.contribution)}`} title={s.detail ?? ''}>
                {s.name}: {String(s.value)}
              </span>
            ))}
          </div>
        ) : <p className="text-slate-400 text-sm">No signals.</p>}
      </Card>

      {/* Audit trail — the transition history */}
      <Card title="Audit trail">
        {audit_trail.length ? (
          <table className="w-full text-xs">
            <thead className="text-slate-500 uppercase text-left"><tr><th className="py-1 pr-3">When</th><th className="py-1 pr-3">Actor</th><th className="py-1 pr-3">Transition</th><th className="py-1 pr-3">Band</th><th className="py-1 pr-3">Reason</th><th className="py-1 pr-3">AI rec</th><th className="py-1">Followed</th></tr></thead>
            <tbody>
              {audit_trail.map((a, i) => (
                <tr key={i} className="border-t border-slate-700/60 text-slate-300">
                  <td className="py-1.5 pr-3 text-slate-400">{new Date(a.created_at).toLocaleString()}</td>
                  <td className="py-1.5 pr-3">{a.actor_id}{a.role_at_action ? ` (${a.role_at_action})` : ''}</td>
                  <td className="py-1.5 pr-3">{a.from_status} → {a.to_status}</td>
                  <td className="py-1.5 pr-3">{a.risk_band_at_action ?? '—'}</td>
                  <td className="py-1.5 pr-3">{a.reason ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-sky-300">{a.ai_recommendation ?? '—'}</td>
                  <td className="py-1.5">{a.followed_ai === true ? '✓' : a.followed_ai === false ? '✗' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-slate-400 text-sm">No transitions yet.</p>}
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Redemption history */}
        <Card title="Redemption history">
          <MiniList
            items={redemption_history}
            empty="No prior redemptions."
            render={(h) => `$${h.usd_value.toFixed(2)} · ${h.status} · ${new Date(h.created_at).toLocaleDateString()}`}
            keyOf={(h) => h.id}
          />
        </Card>
        {/* Sessions */}
        <Card title="Recent sessions">
          <MiniList
            items={sessions}
            empty="No sessions."
            render={(s) => `${s.exchange_name} · ${s.status}${s.points_credited ? ` · +${s.points_credited}` : ''}`}
            keyOf={(s) => s.session_id}
          />
        </Card>
        {/* Support chats */}
        <Card title={`Support chats · ${support_chats.total_grants} pts granted (${support_chats.chat_count})`}>
          <MiniList
            items={support_chats.recent}
            empty="No support chats."
            render={(c) => `${c.issue_type} · ${c.resolution}${c.points_granted ? ` · +${c.points_granted}` : ''}`}
            keyOf={(c) => c.id}
          />
        </Card>
        {/* Trust log */}
        <Card title="Trust log (recent)">
          <MiniList
            items={trust_log}
            empty="No trust changes."
            render={(t) => `${t.delta > 0 ? '+' : ''}${t.delta} · ${t.reason} → ${t.score_after}`}
            keyOf={(t, i) => `${t.created_at}-${i}`}
          />
        </Card>
      </div>

      {/* Devices */}
      <Card title={`Devices · ${devices.distinct_fingerprints} distinct fingerprints`}>
        <MiniList
          items={devices.recent}
          empty="No devices recorded."
          render={(d) => `${d.fingerprint_hash.slice(0, 16)}… · last seen ${new Date(d.last_seen_at).toLocaleDateString()}`}
          keyOf={(d) => d.fingerprint_hash}
        />
      </Card>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-slate-900 border border-slate-700 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-lg">{toast}</div>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="flex justify-between gap-4"><dt className="text-slate-500">{k}</dt><dd className="text-slate-200 text-right">{v}</dd></div>;
}

function MiniList<T>({ items, empty, render, keyOf }: { items: T[]; empty: string; render: (x: T) => string; keyOf: (x: T, i: number) => string }) {
  if (!items.length) return <p className="text-slate-400 text-sm">{empty}</p>;
  return (
    <ul className="text-xs text-slate-300 space-y-1">
      {items.map((x, i) => <li key={keyOf(x, i)} className="truncate">{render(x)}</li>)}
    </ul>
  );
}
